import { useState } from "react";
import { Heart, PartyPopper, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Confetti } from "@/components/dashboard/Confetti";
import { RewardMoment } from "@/components/motion/RewardMoment";
import { StaggerItem, StaggerList } from "@/components/motion/Stagger";
import { useMarkNoticeRead, useMyNotices } from "@/hooks/useGoals";
import { formatRelative } from "@/lib/format";

/**
 * Dismissible cards for parent-sent nudges + goal-achieved celebrations
 * (staxed_student_notices). One-directional by design -- there's no reply
 * here, just encouragement showing up where the child will see it.
 */
export function NoticeStack() {
  const notices = useMyNotices();
  const markRead = useMarkNoticeRead();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const visible = (notices.data ?? []).filter(
    (n) => !n.read && !dismissed.has(n.created_at_notice_id),
  );
  if (visible.length === 0) return null;

  // Fire one celebratory burst if any unread goal-achievement notice is
  // showing -- keyed on its id so it plays once, not on every re-render.
  const achievement = visible.find((n) => n.template_id === "goal_achieved");

  const onDismiss = (noticeId: string) => {
    setDismissed((prev) => new Set(prev).add(noticeId));
    markRead.mutate(noticeId);
  };

  return (
    <div className="relative">
      {achievement ? (
        <Confetti trigger={achievement.created_at_notice_id} origin={{ x: "50%", y: "0%" }} label="Goal achieved!" />
      ) : null}
      <StaggerList className="flex flex-col gap-2">
      {visible.map((n) => {
        const isAchievement = n.template_id === "goal_achieved";
        const icon = (
          <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg bg-brand-soft text-brand">
            {isAchievement ? <PartyPopper className="h-4 w-4" /> : <Heart className="h-4 w-4" />}
          </span>
        );
        return (
          <StaggerItem key={n.created_at_notice_id}>
            <Card className={isAchievement ? "p-4 border-success/40" : "p-4"}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2.5 min-w-0">
                  {isAchievement ? <RewardMoment intensity="subtle">{icon}</RewardMoment> : icon}
                  <div className="min-w-0">
                    <p className="text-sm">{n.custom_line || n.template_text}</p>
                    <p className="text-[11px] text-text-subtle mt-0.5">{formatRelative(n.created_at)}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onDismiss(n.created_at_notice_id)}
                  className="text-text-subtle hover:text-text flex-shrink-0"
                  aria-label="Dismiss"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </Card>
          </StaggerItem>
        );
      })}
      </StaggerList>
    </div>
  );
}
